import { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import IconCircleButton from '../../components/UISystem/Icons/IconCircleButton';
import PostCard from '../../components/Posts/PostCard';
import PostComposerSheet from '../../components/Posts/PostComposerSheet';
import useFeed from '../../hooks/useFeed';
import { createPost, uploadPostPhoto, extractFirstUrl } from '../../services/posts/postsService';
import { getProfileUserId } from '../../utils/identityUtils';
import { logEvent, logPostsViewed } from '../../services/analytics/eventLogger';

/**
 * PostsFeedPage — the Posts tab (Phase 1 broadcast feed).
 * Design: 2026-07-13_posts/posts-home.png.
 *
 * Feed of connections' posts (own posts included), newest first. Freshness is
 * refetch on mount + app foreground + after compose (useFeed — no realtime on
 * posts by design). Presentation layer: throwaway; the data layer lives in
 * useFeed/postsService.
 */

/**
 * Fun empty state — house rule. First of its kind in the app; establishes the
 * pattern (big glyph + warm headline + the action that fixes the emptiness).
 */
const EmptyFeed = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: '80px 32px 0',
      gap: '10px',
    }}
  >
    <div style={{ fontSize: '44px' }}>🦗</div>
    <div style={{ fontSize: '18px', fontWeight: 700, color: '#000000' }}>Crickets. For now.</div>
    <div style={{ fontSize: '15px', color: '#8E8E93', lineHeight: 1.45 }}>
      Posts from your people land here.
      <br />
      Tap <strong>+</strong> and go first — someone has to.
    </div>
  </div>
);

const PostsFeedPage = () => {
  const { posts, loaded, hasMore, loadingMore, refresh, loadMore, toggleLike } = useFeed();
  const [composerOpen, setComposerOpen] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    logPostsViewed();
  }, []);

  // Infinite scroll: fetch the next keyset page when the sentinel nears view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  // Card passes its own row — keeps this callback dep-stable so memo(PostCard)
  // holds and a like tap re-renders one card, not the feed.
  const handleToggleLike = useCallback(
    (post) => {
      if (!post.viewer_liked) {
        logEvent('post_liked', { postId: post.id, authorId: post.author_id });
      }
      toggleLike(post.id);
    },
    [toggleLike]
  );

  // Compose: upload the photo at Done (never at selection), then create the
  // post, then refresh so the new post arrives at the top from the DB.
  // Throws surface in the sheet (stays open, inline error) — a failed upload
  // must never create a post with a dangling photo_url.
  const handlePost = useCallback(
    async ({ text, photoFile, locationSnapshot }) => {
      const authorId = getProfileUserId();
      let photoUrl = null;
      if (photoFile) {
        photoUrl = await uploadPostPhoto(photoFile, authorId);
      }
      await createPost({ authorId, text, photoUrl, locationSnapshot });
      logEvent('post_created', {
        hasText: !!text?.trim(),
        hasPhoto: !!photoUrl,
        hasUrl: !!extractFirstUrl(text),
        hasLocation: !!locationSnapshot,
      });
      refresh();
    },
    [refresh]
  );

  return (
    // Fragment on purpose: the composer sheet must be a SIBLING of the fixed
    // page root, not a child — position:fixed creates a stacking context that
    // would trap the sheet's z-index below the tab bar (zIndex 90).
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#FFFFFF',
        }}
      >
        {/* Header — matches the tab-page header treatment (fixed, safe-area) */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: '#FFFFFF',
            zIndex: 100,
          }}
        >
          <div style={{ height: 'env(safe-area-inset-top)', backgroundColor: '#FFFFFF' }} />
          <div
            style={{
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              boxSizing: 'border-box',
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '32px',
                fontWeight: '800',
                color: '#000000',
              }}
            >
              Posts
            </span>
            {/* Black + = compose (CTA color convention §8.3: black when action invited) */}
            <IconCircleButton
              icon={Plus}
              size={22}
              buttonSize={44}
              backgroundColor="#000000"
              iconColor="#FFFFFF"
              iconWeight="bold"
              onClick={() => setComposerOpen(true)}
            />
          </div>
        </div>

        {/* Feed */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingTop: 'calc(env(safe-area-inset-top) + 68px)',
            paddingBottom: '100px', // clear the tab bar
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              boxSizing: 'border-box',
              padding: '4px 16px 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {/* Empty only when loaded — loading ≠ empty */}
            {loaded && posts.length === 0 && <EmptyFeed />}
            {posts.map((post) => (
              <PostCard key={post.id} post={post} onToggleLike={handleToggleLike} />
            ))}
            {hasMore && <div ref={sentinelRef} style={{ height: '1px' }} />}
            {loadingMore && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px',
                  fontSize: '14px',
                  color: '#8E8E93',
                }}
              >
                Loading…
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {composerOpen && (
          <PostComposerSheet onPost={handlePost} onClose={() => setComposerOpen(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default PostsFeedPage;
